import type { PropsWithChildren } from "react";
import { useCallback, useEffect, useState } from "react";

import { type ResponsiveImageProps } from "@ui/components/ResponsiveImage";
import { RichText, type RichTextPropsContent } from "@ui/eva-components/RichText";
import { useDeviceBreakpoint } from "@ui/hooks/useDeviceBreakpoint";
import { useUser } from "@ui/hooks/useUser";
import classNames from "classnames";

import { Button } from "@jti/core-components/components/Button";
import { PictureBehaviour } from "@jti/core-components/components/PictureBehaviour";
import { getProvinceCookies, setProvinceAcceptedCookie } from "@jti/fe-common";
import { getMessageBus } from "@jti/fe-message-bus";

import type { DialogProps } from "../../components/Dialog";
import { BACKGROUND_OPTIONS, Dialog } from "../../components/Dialog";

import styles from "./ProvincePopup.module.scss";

export type ButtonProps = {
  cta?: string;
  href?: string;
  isButtonEnabled?: boolean;
};

export type ProvincePopupProps = {
  primaryButton: ButtonProps;
  secondaryButton?: ButtonProps;
  isAuthorMode?: boolean;
  image?: ResponsiveImageProps;
  logo?: { src: string; alt?: string };
  paragraph?: RichTextPropsContent;
  backgroundColor?: keyof typeof BACKGROUND_OPTIONS;
  isLogoEnabled?: boolean;
  isParagraphEnabled?: boolean;
  isImageEnabled?: boolean;
} & Pick<DialogProps, "portalDomNode">;

export const ProvincePopup = ({
  portalDomNode = document.body,
  image,
  backgroundColor = "blurredWhite",
  logo,
  paragraph,
  isAuthorMode,
  isLogoEnabled = true,
  isParagraphEnabled = true,
  isImageEnabled = true,
  primaryButton,
  secondaryButton,
}: PropsWithChildren<ProvincePopupProps>) => {
  const [isOpen, setIsOpen] = useState(false);
  const { isUserLoggedIn } = useUser();
  const { province, provinceAccepted } = getProvinceCookies();
  const { breakpoint } = useDeviceBreakpoint();

  const showTopSection = isImageEnabled && isParagraphEnabled;
  const showSecondaryButton = secondaryButton?.isButtonEnabled && Boolean(secondaryButton.href);

  useEffect(() => {
    if (isUserLoggedIn && Boolean(province) && !provinceAccepted) {
      setIsOpen(true);
    }
  }, [isUserLoggedIn, province, provinceAccepted]);

  useEffect(() => {
    if (isAuthorMode) return;

    const messageBus = getMessageBus();
    messageBus.publish("province.popup.state", { payload: { isOpen } }, { sticky: true });

    return () => {
      messageBus.publish("province.popup.state", { payload: { isOpen: false } }, { sticky: true });
    };
  }, [isAuthorMode, isOpen]);

  const closePopupAndReload = useCallback(() => {
    setIsOpen(false);
    window.location.reload();
  }, []);

  const handlePrimaryClick = useCallback(() => {
    setProvinceAcceptedCookie();
    closePopupAndReload();
  }, [closePopupAndReload]);

  return (
    <Dialog
      dataTestId="province-dialog"
      closable={false}
      isOpened={isAuthorMode || isOpen}
      fullscreen={breakpoint === "mobile"}
      backgroundBlur={BACKGROUND_OPTIONS[backgroundColor].blur}
      backgroundColor={BACKGROUND_OPTIONS[backgroundColor].color}
      className={classNames(styles.dialog, styles.provincePopup)}
      isUnwrapped={isAuthorMode}
      withContentPadding={false}
      portalDomNode={portalDomNode}
    >
      <div className={styles.popupContent}>
        {showTopSection && (
          <div className={styles.topSection}>
            {isLogoEnabled && logo?.src && (
              <div className={styles.logoWrapper}>
                <img src={logo.src} alt={logo?.alt} className={styles.logo} />
              </div>
            )}
            {isParagraphEnabled && paragraph && <RichText className={styles.paragraph} {...paragraph} />}
          </div>
        )}
        {isImageEnabled && image && (
          <PictureBehaviour
            nativeBehaviour={true}
            className={styles.imageSection}
            sources={[
              { srcSet: image.smallSrc, media: "(max-width: 767px)" },
              { srcSet: image.mediumSrc, media: "(min-width: 768px) and (max-width: 960px)" },
            ]}
            imageProps={{
              src: image.largeSrc,
              alt: image.alt,
              className: styles.image,
            }}
          />
        )}
        <div className={styles.buttonsWrapper}>
          <Button variant="primary" onClick={handlePrimaryClick}>
            {primaryButton.cta ?? ""}
          </Button>
          {showSecondaryButton && (
            <Button href={secondaryButton.href} variant="secondary">
              {secondaryButton.cta ?? ""}
            </Button>
          )}
        </div>
      </div>
    </Dialog>
  );
};
